import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { 
  Search, 
  Heart, 
  Clock, 
  Plus,
  Eye,
  Loader2,
  X,
  Calendar,
  ChevronRight,
  Sparkles,
  Star
} from 'lucide-react';
import axios from 'axios';
import { haptic } from '@/utils/haptics';
import { 
  trackRecipeSearched, 
  trackSearchChipUsed,
  trackDemoStepViewed 
} from '@/utils/analytics';
import HealthLabelBadges from '@/components/HealthLabelBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RESULTS_PER_PAGE = 30;

// Smart suggestion chips
const SUGGESTION_CHIPS = [
  { id: 'quick', label: 'Under 30 min', emoji: '🍗', query: 'quick chicken' },
  { id: 'family', label: 'Family friendly', emoji: '🧒', query: 'pasta family' },
  { id: 'pantry', label: 'Use pantry items', emoji: '🥦', query: 'rice beans' },
  { id: 'comfort', label: 'Comfort food', emoji: '🍝', query: 'soup stew' },
  { id: 'popular', label: 'Popular', emoji: '⭐', query: 'chicken beef' },
];

// Days for meal planning
const DAYS = [
  { id: 'tonight', label: 'Tonight', getDate: () => new Date() },
  { id: 'tomorrow', label: 'Tomorrow', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 1); return d; } },
  { id: 'pick', label: 'Pick a day', getDate: null },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Mobile-optimized Recipe Search Component
 * Goal: Help users find "something good for tonight" in under 10 seconds
 */
const MobileRecipeSearch = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDemoMode = searchParams.get('demo') === 'true';
  const searchInputRef = useRef(null);
  
  // Search state
  const [query, setQuery] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [maxReadyTime, setMaxReadyTime] = useState(null);
  const [diet, setDiet] = useState('');
  const [cuisine, setCuisine] = useState('');
  
  // Infinite scroll state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Add to Plan sheet state
  const [addToPlanSheetOpen, setAddToPlanSheetOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [addingToPlan, setAddingToPlan] = useState(false);

  // Track demo step viewed
  useEffect(() => {
    if (isDemoMode) {
      trackDemoStepViewed(1, 'Find Recipes');
    }
  }, [isDemoMode]);

  // Load user's favorites
  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get('/favorites');
      const favIds = new Set(response.data.map(f => f.recipe_id));
      setFavorites(favIds);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Search handler
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    
    setPage(1);
    setRecipes([]);
    setHasMore(true);
    await performSearch(query, 1, false);
  };

  // Chip search handler
  const handleChipSearch = (chip) => {
    haptic('light');
    trackSearchChipUsed(chip.label, isDemoMode);
    setQuery(chip.label);
    setPage(1);
    setRecipes([]);
    setHasMore(true);
    performSearch(chip.query, 1, false);
  };

  // Perform search
  const performSearch = async (searchQuery, pageNum, append = false, filters = {}) => {
    if (!searchQuery.trim()) return;
    
    // Use overrides if provided, otherwise use state
    const effectiveMaxTime = filters.maxReadyTime !== undefined ? filters.maxReadyTime : maxReadyTime;
    const effectiveDiet = filters.diet !== undefined ? filters.diet : diet;
    const effectiveCuisine = filters.cuisine !== undefined ? filters.cuisine : cuisine;
    
    trackRecipeSearched(searchQuery, isDemoMode);
    
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setSearched(true);

    try {
      const offset = (pageNum - 1) * RESULTS_PER_PAGE;
      const response = isAuthenticated 
        ? await api.post('/recipes/search', {
            query: searchQuery.trim(),
            number: RESULTS_PER_PAGE,
            offset: offset,
            diet: effectiveDiet || null,
            cuisine: effectiveCuisine || null,
            maxReadyTime: effectiveMaxTime || null
          })
        : await axios.post(`${API_URL}/api/recipes/search`, {
            query: searchQuery.trim(),
            number: RESULTS_PER_PAGE,
            offset: offset,
            diet: effectiveDiet || null,
            cuisine: effectiveCuisine || null,
            maxReadyTime: effectiveMaxTime || null
          });

      const newRecipes = response.data.results || [];
      const total = response.data.totalResults || 0;
      
      if (append) {
        setRecipes(prev => [...prev, ...newRecipes]);
      } else {
        setRecipes(newRecipes);
      }
      
      setHasMore(offset + newRecipes.length < total);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search recipes');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more for infinite scroll
  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    performSearch(query, nextPage, true);
  };

  // Infinite scroll observer
  useEffect(() => {
    if (!searched || loading || loadingMore || !hasMore) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const sentinel = document.getElementById('scroll-sentinel');
    if (sentinel) observer.observe(sentinel);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, loading, loadingMore, hasMore, page]);

  // Toggle favorite
  const toggleFavorite = async (recipe, e) => {
    e?.stopPropagation();
    haptic('medium');
    
    if (!isAuthenticated) {
      if (isDemoMode) {
        toast.info('Sign up to save this recipe!');
      } else {
        toast.info('Sign up to save recipes!');
        setTimeout(() => navigate('/auth'), 1500);
      }
      return;
    }
    
    const recipeId = recipe.id;
    const isFavorite = favorites.has(recipeId);

    try {
      if (isFavorite) {
        await api.delete(`/favorites/${recipeId}`);
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(recipeId);
          return next;
        });
        toast.success('Removed from favorites');
      } else {
        await api.post('/favorites', {
          recipe_id: recipeId,
          recipe_title: recipe.title,
          recipe_image: recipe.image,
          ready_in_minutes: recipe.readyInMinutes,
          servings: recipe.servings
        });
        setFavorites(prev => new Set([...prev, recipeId]));
        haptic('success');
        toast.success('Saved to Favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  // Add to Plan handlers
  const handleAddToPlanTap = (recipe, e) => {
    e?.stopPropagation();
    haptic('medium');
    
    if (!isAuthenticated) {
      if (isDemoMode) {
        // In demo mode, navigate to meal planner
        navigate('/meal-planner?demo=true');
        return;
      }
      toast.info('Sign up to plan meals!');
      setTimeout(() => navigate('/auth'), 1500);
      return;
    }
    
    setSelectedRecipe(recipe);
    setShowDayPicker(false);
    setAddToPlanSheetOpen(true);
  };

  const handleDaySelect = async (dayOption) => {
    if (dayOption.id === 'pick') {
      setShowDayPicker(true);
      return;
    }
    await addRecipeToDay(dayOption.getDate());
  };

  const handleSpecificDayPick = async (dayIndex) => {
    const today = new Date();
    const targetDate = new Date(today);
    const currentDay = today.getDay();
    const daysToAdd = (dayIndex - currentDay + 7) % 7;
    targetDate.setDate(today.getDate() + daysToAdd);
    await addRecipeToDay(targetDate);
  };

  const addRecipeToDay = async (date) => {
    if (!selectedRecipe) return;
    
    setAddingToPlan(true);
    haptic('medium');
    
    try {
      const dateStr = date.toISOString().split('T')[0];
      
      // Get current meal plan
      const currentPlanRes = await api.get('/meal-plans');
      const currentPlan = currentPlanRes.data?.days || [];
      
      // Create new meal
      const newMeal = {
        recipe_id: selectedRecipe.id,
        recipe_title: selectedRecipe.title,
        recipe_image: selectedRecipe.image,
        servings: 1
      };
      
      // Add to plan
      const existingDayIndex = currentPlan.findIndex(d => d.date === dateStr);
      let updatedDays;
      
      if (existingDayIndex >= 0) {
        updatedDays = currentPlan.map((day, idx) => 
          idx === existingDayIndex 
            ? { ...day, meals: [...day.meals, newMeal] }
            : day
        );
      } else {
        updatedDays = [...currentPlan, { date: dateStr, meals: [newMeal] }];
      }
      
      await api.post('/meal-plans', { days: updatedDays });
      
      toast.success(`Added to ${WEEKDAYS[date.getDay()]}!`, { duration: 2000 });
      haptic('success');
      setAddToPlanSheetOpen(false);
      setSelectedRecipe(null);
    } catch (error) {
      console.error('Failed to add to meal plan:', error);
      toast.error('Failed to add to plan');
    } finally {
      setAddingToPlan(false);
    }
  };

  // Soft stop check
  const showSoftStop = (index) => {
    return index > 0 && (index + 1) % 10 === 0;
  };

  return (
    <div className="min-h-screen pb-24" data-testid="mobile-recipe-search">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b px-4 py-4">
        <h1 className="text-xl font-semibold font-['Playfair_Display'] mb-1">
          Browse Recipes
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Find a meal for tonight or save it for later.
        </p>

        {/* Search Bar */}
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search by dish, ingredient, or mood"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 pr-10 h-12 text-base rounded-full bg-muted/50"
              data-testid="search-input"
            />
            {query && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8"
                onClick={() => { setQuery(''); searchInputRef.current?.focus(); }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Smart "Start Here" Section - Before Search */}
        {!searched && !loading && (
          <div className="mb-6" data-testid="start-here-section">
            <p className="text-sm font-medium text-muted-foreground mb-3">Start here</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTION_CHIPS.map((chip) => (
                <Button
                  key={chip.id}
                  variant="outline"
                  size="sm"
                  className="h-10 px-4 rounded-full bg-background active:scale-[0.98]"
                  onClick={() => handleChipSearch(chip)}
                  data-testid={`chip-${chip.id}`}
                >
                  <span className="mr-2">{chip.emoji}</span>
                  {chip.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Finding recipes...</p>
          </div>
        )}

        {/* Results */}
        {searched && !loading && (
          <>
            {/* Quick Filters */}
            <div className="space-y-2 mb-4" data-testid="quick-filters-section">
              {/* Time Filters */}
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 15, label: '15 min', icon: '⚡' },
                  { value: 30, label: '30 min', icon: '🍳' },
                  { value: 45, label: '45 min', icon: '👨‍🍳' },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    variant={maxReadyTime === opt.value ? "default" : "outline"}
                    size="sm"
                    className="h-8 rounded-full"
                    onClick={() => {
                      haptic('light');
                      const newTime = maxReadyTime === opt.value ? null : opt.value;
                      setMaxReadyTime(newTime);
                      setPage(1);
                      setRecipes([]);
                      if (query) {
                        performSearch(query, 1, false, { maxReadyTime: newTime });
                      }
                    }}
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    {opt.label}
                  </Button>
                ))}
              </div>

              {/* Diet & Cuisine Filters */}
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'vegetarian', label: 'Vegetarian', icon: '🥗', type: 'diet' },
                  { value: 'vegan', label: 'Vegan', icon: '🌱', type: 'diet' },
                  { value: 'gluten free', label: 'GF', icon: '🌾', type: 'diet' },
                  { value: 'italian', label: 'Italian', icon: '🇮🇹', type: 'cuisine' },
                  { value: 'mexican', label: 'Mexican', icon: '🇲🇽', type: 'cuisine' },
                  { value: 'chinese', label: 'Chinese', icon: '🇨🇳', type: 'cuisine' },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    variant={(opt.type === 'diet' ? diet : cuisine) === opt.value ? "default" : "outline"}
                    size="sm"
                    className="h-8 rounded-full"
                    onClick={() => {
                      haptic('light');
                      if (opt.type === 'diet') {
                        const newDiet = diet === opt.value ? '' : opt.value;
                        setDiet(newDiet);
                        setPage(1);
                        setRecipes([]);
                        if (query) {
                          performSearch(query, 1, false, { diet: newDiet });
                        }
                      } else {
                        const newCuisine = cuisine === opt.value ? '' : opt.value;
                        setCuisine(newCuisine);
                        setPage(1);
                        setRecipes([]);
                        if (query) {
                          performSearch(query, 1, false, { cuisine: newCuisine });
                        }
                      }
                    }}
                  >
                    <span className="mr-1">{opt.icon}</span>
                    {opt.label}
                  </Button>
                ))}
              </div>

              {/* Clear Filters */}
              {(maxReadyTime || diet || cuisine) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-full text-muted-foreground"
                  onClick={() => {
                    haptic('light');
                    setMaxReadyTime(null);
                    setDiet('');
                    setCuisine('');
                    setPage(1);
                    setRecipes([]);
                    if (query) {
                      performSearch(query, 1, false, { maxReadyTime: null, diet: '', cuisine: '' });
                    }
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear filters
                </Button>
              )}
            </div>

            {recipes.length > 0 ? (
              <div className="space-y-3">
                {recipes.map((recipe, index) => (
                  <div key={`${recipe.id}-${index}`}>
                    <RecipeCard
                      recipe={recipe}
                      isFavorite={favorites.has(recipe.id)}
                      onFavorite={(e) => toggleFavorite(recipe, e)}
                      onAddToPlan={(e) => handleAddToPlanTap(recipe, e)}
                      onView={() => navigate(`/recipes/${recipe.id}`)}
                    />
                    
                    {/* Soft Stop */}
                    {showSoftStop(index) && (
                      <div className="my-6 p-4 rounded-xl bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 text-center">
                        <p className="text-sm font-medium mb-3">Found something you like?</p>
                        <Button
                          size="sm"
                          onClick={() => navigate('/meal-planner')}
                          data-testid="soft-stop-cta"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add meals to your week
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Scroll Sentinel for Infinite Scroll */}
                {hasMore && (
                  <div id="scroll-sentinel" className="py-8 flex justify-center">
                    {loadingMore && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
                  </div>
                )}
                
                {/* End of Results */}
                {!hasMore && recipes.length > 0 && (
                  <div className="py-8 text-center">
                    <p className="text-sm text-muted-foreground mb-4">You&apos;ve seen all {recipes.length} recipes</p>
                    <Button variant="outline" onClick={() => { setSearched(false); setQuery(''); setRecipes([]); }}>
                      Search for something else
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* Empty Results */
              <div className="text-center py-12" data-testid="no-results">
                <div className="text-4xl mb-4">🤔</div>
                <h3 className="text-lg font-medium mb-2">No matches found</h3>
                <p className="text-muted-foreground mb-6 text-sm">
                  Try fewer filters or search for something else
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['chicken', 'pasta', 'quick dinner'].map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => { setQuery(suggestion); performSearch(suggestion, 1, false); }}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Initial Empty State */}
        {!searched && !loading && (
          <div className="text-center py-8">
            <Sparkles className="h-12 w-12 text-primary/30 mx-auto mb-4" />
            <p className="text-muted-foreground">
              Tap a suggestion above or search for anything
            </p>
          </div>
        )}
      </div>

      {/* Add to Plan Bottom Sheet */}
      <Sheet open={addToPlanSheetOpen} onOpenChange={setAddToPlanSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8">
          <SheetHeader className="text-left pb-4">
            <SheetTitle className="text-xl font-['Playfair_Display']">
              Add to your week
            </SheetTitle>
            {selectedRecipe && (
              <SheetDescription className="flex items-center gap-2">
                <img 
                  src={selectedRecipe.image} 
                  alt="" 
                  className="h-8 w-8 rounded object-cover"
                />
                {selectedRecipe.title}
              </SheetDescription>
            )}
          </SheetHeader>

          {!showDayPicker ? (
            <div className="space-y-3">
              {DAYS.map((day) => (
                <Button
                  key={day.id}
                  variant="outline"
                  className="w-full h-14 justify-between text-left active:scale-[0.98]"
                  onClick={() => handleDaySelect(day)}
                  disabled={addingToPlan}
                  data-testid={`day-option-${day.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{day.label}</span>
                  </div>
                  {addingToPlan ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                </Button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDayPicker(false)}
                className="mb-2"
              >
                ← Back
              </Button>
              <div className="grid grid-cols-2 gap-2">
                {WEEKDAYS.map((day, index) => (
                  <Button
                    key={day}
                    variant="outline"
                    className="h-12 active:scale-[0.98]"
                    onClick={() => handleSpecificDayPick(index)}
                    disabled={addingToPlan}
                    data-testid={`weekday-${day.toLowerCase()}`}
                  >
                    {day}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

/**
 * Compact Recipe Card Component
 */
const RecipeCard = ({ recipe, isFavorite, onFavorite, onAddToPlan, onView }) => {
  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50 transition-all active:scale-[0.99]"
      data-testid={`recipe-card-${recipe.id}`}
    >
      {/* Thumbnail */}
      <div 
        className="relative flex-shrink-0 cursor-pointer"
        onClick={onView}
      >
        <img
          src={recipe.image}
          alt={recipe.title}
          className="h-20 w-20 rounded-lg object-cover"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0" onClick={onView}>
        <h3 className="font-medium text-sm line-clamp-2 mb-1 cursor-pointer">
          {recipe.title}
        </h3>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1.5">
          {recipe.readyInMinutes && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {recipe.readyInMinutes} min
            </span>
          )}
          {recipe.spoonacularScore && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {(recipe.spoonacularScore / 20).toFixed(1)}
            </span>
          )}
        </div>
        {/* Health Labels */}
        <HealthLabelBadges 
          healthLabels={recipe.healthLabels || []}
          dietLabels={recipe.dietLabels || []}
          maxDisplay={2}
          size="xs"
          showTooltip={false}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Favorite */}
        <Button
          variant="ghost"
          size="icon"
          className={`h-9 w-9 ${isFavorite ? 'text-red-500' : 'text-muted-foreground'}`}
          onClick={onFavorite}
          data-testid={`favorite-${recipe.id}`}
        >
          <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
        </Button>

        {/* Add to Plan */}
        <Button
          size="sm"
          className="h-9 px-3 bg-primary"
          onClick={onAddToPlan}
          data-testid={`add-to-plan-${recipe.id}`}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
};

export default MobileRecipeSearch;
